import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatCOP, formatDate, formatApiError } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import ConfirmDialog from "../components/ConfirmDialog";
import { useConfirm } from "../components/useConfirm";
import { toast } from "sonner";
import { SecopSearchTrace } from "../components/n1/SecopSearchTrace";

function Badge({ tone, children, testid }) {
  const tones = {
    verified: "bg-emerald-50 text-emerald-800 border-emerald-300",
    manual: "bg-blue-50 text-blue-800 border-blue-300",
    unverified: "bg-amber-50 text-amber-900 border-amber-300",
    info: "bg-gray-100 text-gray-700 border-gray-300",
  };
  return (
    <span
      data-testid={testid}
      className={`inline-flex border px-2 py-[3px] text-[11px] uppercase tracking-wider ${tones[tone] || tones.info}`}
    >
      {children}
    </span>
  );
}

export default function AdminCaseSecopPage() {
  const { id } = useParams();
  const [caseDoc, setCaseDoc] = useState(null);
  const [stored, setStored] = useState({ items: [], limitation: null, count: 0 });
  const [loadingStored, setLoadingStored] = useState(true);

  // Search form
  const [keywords, setKeywords] = useState("");
  const [dataset, setDataset] = useState("both");
  const [includeAux, setIncludeAux] = useState(false);
  const [valorMin, setValorMin] = useState("");
  const [valorMax, setValorMax] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [searchMeta, setSearchMeta] = useState(null);
  const [searching, setSearching] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(new Set());

  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({
    entidad: "", objeto: "", valor_cop: "", adjudicatario: "",
    fecha: "", source_url: "", modalidad: "", notes: "",
  });

  const [limitationOpen, setLimitationOpen] = useState(false);
  const [limitationReason, setLimitationReason] = useState("");

  // Iter24.d: confirm dialog (reemplaza window.confirm)
  const { confirm, dialogProps: confirmDialogProps } = useConfirm();

  // Load
  const loadStored = useCallback(async () => {
    setLoadingStored(true);
    try {
      const { data } = await api.get(`/admin/cases/${id}/secop/comparables`);
      setStored(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se cargaron comparables");
    } finally {
      setLoadingStored(false);
    }
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        const { data: c } = await api.get(`/cases/${id}`);
        setCaseDoc(c);
      } catch (e) {
        toast.error("No se cargó el caso");
      }
      await loadStored();
    })();
  }, [id, loadStored]);

  const onSearch = async () => {
    setSearching(true);
    setSelectedIdx(new Set());
    try {
      const params = new URLSearchParams();
      if (keywords) params.set("keywords", keywords);
      params.set("dataset", dataset);
      params.set("limit", "10");
      if (valorMin) params.set("valor_min", valorMin);
      if (valorMax) params.set("valor_max", valorMax);
      if (includeAux) params.set("include_aux_datasets", "true");
      const { data } = await api.get(`/admin/cases/${id}/secop/search?${params}`);
      setCandidates(data.candidates || []);
      setSearchMeta(data);
      if (!keywords && data?.meta?.keywords_used) {
        setKeywords(data.meta.keywords_used);
      }
      if (data.fallback_message) toast.warning(data.fallback_message);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error de búsqueda");
    } finally {
      setSearching(false);
    }
  };

  const toggleIdx = (i) => {
    const s = new Set(selectedIdx);
    if (s.has(i)) s.delete(i); else s.add(i);
    setSelectedIdx(s);
  };

  const onSaveSelection = async () => {
    if (selectedIdx.size === 0) return;
    const items = Array.from(selectedIdx).map((i) => candidates[i]).map((c) => ({
      dataset: c.dataset,
      entidad: c.entidad,
      objeto: c.objeto,
      valor_cop: c.valor_cop,
      adjudicatario: c.adjudicatario,
      fecha: c.fecha,
      source_url: c.source_url,
      secop_native_id: c.secop_native_id,
      modalidad: c.modalidad,
    }));
    try {
      await api.post(`/admin/cases/${id}/secop/select`, { comparables: items });
      toast.success(`${items.length} comparables guardados`);
      setSelectedIdx(new Set());
      await loadStored();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo guardar");
    }
  };

  const onAddManual = async () => {
    try {
      const payload = {
        entidad: manualForm.entidad.trim(),
        objeto: manualForm.objeto.trim(),
        source_url: manualForm.source_url.trim(),
      };
      if (manualForm.valor_cop) payload.valor_cop = Number(manualForm.valor_cop);
      if (manualForm.adjudicatario) payload.adjudicatario = manualForm.adjudicatario.trim();
      if (manualForm.fecha) payload.fecha = manualForm.fecha;
      if (manualForm.modalidad) payload.modalidad = manualForm.modalidad.trim();
      if (manualForm.notes) payload.notes = manualForm.notes.trim();
      await api.post(`/admin/cases/${id}/secop/manual`, payload);
      toast.success("Comparable manual agregado");
      setManualOpen(false);
      setManualForm({ entidad: "", objeto: "", valor_cop: "", adjudicatario: "", fecha: "", source_url: "", modalidad: "", notes: "" });
      await loadStored();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo agregar");
    }
  };

  const onDelete = async (cid) => {
    try {
      await api.delete(`/admin/cases/${id}/secop/comparables/${cid}`);
      toast.success("Comparable eliminado");
      await loadStored();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo eliminar");
    }
  };

  const onAcceptLimitation = async () => {
    if ((limitationReason || "").trim().length < 20) {
      toast.error("La razón debe tener al menos 20 caracteres");
      return;
    }
    try {
      await api.post(`/admin/cases/${id}/secop/accept-limitation`, {
        limitation_reason: limitationReason.trim(),
      });
      toast.success("Limitación registrada");
      setLimitationOpen(false);
      setLimitationReason("");
      await loadStored();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo registrar");
    }
  };

  const onReset = async () => {
    const ok = await confirm({
      title: "Reset de comparables",
      description: "¿Borrar TODOS los comparables y limpiar la limitación de este caso? Esta acción es auditada.",
      confirmLabel: "Sí, borrar todo",
      destructive: true,
      testIdPrefix: "secop-reset-confirm",
    });
    if (!ok) return;
    try {
      await api.post(`/admin/cases/${id}/secop/reset`);
      toast.success("Reset OK");
      await loadStored();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo reiniciar");
    }
  };

  const [autoRunning, setAutoRunning] = useState(false);
  const [autoTrace, setAutoTrace] = useState(null);
  const onAutoSearch = async () => {
    const ok = await confirm({
      title: "Búsqueda automática de comparables",
      description: "Reemplaza los comparables guardados por los mejores 3-5 encontrados en SECOP. Esta acción es auditada.",
      confirmLabel: "Buscar y reemplazar",
      testIdPrefix: "secop-auto-confirm",
    });
    if (!ok) return;
    setAutoRunning(true);
    try {
      const { data } = await api.post(`/admin/cases/${id}/secop/auto-search`);
      setAutoTrace(data);
      toast.success(`Búsqueda automática: ${data.selected_count} comparables`);
      await loadStored();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo ejecutar la búsqueda");
    } finally {
      setAutoRunning(false);
    }
  };

  // Iter25.d — SECOP feedback (learning loop).
  const [feedbackMap, setFeedbackMap] = useState({}); // { comparable_id: {useful, rating} }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/cases/${id}/secop/feedback`);
        const m = {};
        (data?.items || []).forEach((f) => {
          if (!m[f.comparable_id] || (!f.implicit && m[f.comparable_id].implicit)) {
            m[f.comparable_id] = { useful: f.useful, rating: f.rating, implicit: f.implicit };
          }
        });
        if (!cancelled) setFeedbackMap(m);
      } catch (e) {
        // 404 ok, sin feedback aún
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const sendFeedback = async (comparableId, useful, rating = null) => {
    try {
      await api.post(`/admin/cases/${id}/secop/comparables/${comparableId}/feedback`, {
        useful, rating,
      });
      setFeedbackMap((prev) => ({ ...prev, [comparableId]: { useful, rating, implicit: false } }));
      toast.success(useful ? "Marcado como útil" : "Marcado como no útil");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo registrar feedback");
    }
  };

  const status = stored.count > 0
    ? `Con ${stored.count} comparable${stored.count !== 1 ? "s" : ""}`
    : stored.limitation?.accepted_limitation
    ? "Sin comparables (limitación aceptada)"
    : "Sin comparables";

  return (
    <div data-testid="admin-case-secop-page" className="container-prose py-12">
      <div className="max-w-5xl mx-auto">
        <div className="mb-2">
          <Link to="/admin" className="text-sm text-gray-500 hover:text-primary">← Volver al panel</Link>
        </div>

        <div className="mb-8 pb-6 border-b border-gray-200">
          <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-2">
            Comparables SECOP
          </p>
          <h1 className="font-serif text-3xl text-gray-900 mb-2">
            {caseDoc?.title || "Caso"}
          </h1>
          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
            <span data-testid="secop-status">{status}</span>
            {caseDoc && <span>{formatCOP(caseDoc.valor_estimado_cop)} · estado {caseDoc.state}</span>}
          </div>
          <p className="text-xs text-gray-500 mt-3 italic">
            Los comparables se usan como contexto del diagnóstico. El sistema nunca inventa comparables.
          </p>
        </div>

        {/* SEARCH */}
        <section className="border border-gray-200 bg-white p-7 mb-8">
          <h2 className="font-serif text-xl text-gray-900 mb-5">Buscar en SECOP</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="md:col-span-2">
              <Label htmlFor="kw">Palabras clave</Label>
              <Input
                id="kw"
                placeholder="Si lo dejas vacío, se derivan del intake"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                data-testid="secop-keywords"
                className="rounded-sm h-11"
              />
            </div>
            <div>
              <Label>Dataset</Label>
              <Select value={dataset} onValueChange={setDataset}>
                <SelectTrigger data-testid="secop-dataset" className="rounded-sm h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Ambos</SelectItem>
                  <SelectItem value="procesos">Procesos</SelectItem>
                  <SelectItem value="contratos">Contratos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Valor mín (COP)</Label>
                <Input type="number" value={valorMin} onChange={(e) => setValorMin(e.target.value)} className="rounded-sm h-11" data-testid="secop-valor-min" />
              </div>
              <div>
                <Label>Valor máx (COP)</Label>
                <Input type="number" value={valorMax} onChange={(e) => setValorMax(e.target.value)} className="rounded-sm h-11" data-testid="secop-valor-max" />
              </div>
            </div>
          </div>
          <Button onClick={onSearch} disabled={searching} className="rounded-sm h-11 px-6" data-testid="secop-search-button">
            {searching ? "Buscando…" : "Buscar en SECOP"}
          </Button>

          <label className="flex items-center gap-2 mt-3 text-xs text-gray-700 cursor-pointer select-none" data-testid="secop-include-aux-label">
            <input
              type="checkbox"
              checked={includeAux}
              onChange={(e) => setIncludeAux(e.target.checked)}
              className="w-3.5 h-3.5"
              data-testid="secop-include-aux-checkbox"
            />
            <span>
              <strong>Multi-dataset (cross-source)</strong> · incluir TVEC, PAA, SECOP I, Adjudicaciones
              para histórico cruzado. <span className="text-gray-500 italic">Aumenta la latencia ~2x pero amplía los comparables.</span>
            </span>
          </label>

          {searchMeta && (
            <div className="mt-5">
              {searchMeta.fallback_message && (
                <div className="border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 text-sm mb-4" data-testid="secop-fallback">
                  {searchMeta.fallback_message}
                </div>
              )}
              {candidates.length > 0 && (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs text-gray-500">
                      {candidates.length} candidatos · {selectedIdx.size} seleccionado{selectedIdx.size !== 1 ? "s" : ""}
                      {searchMeta.from_cache && " · desde cache"}
                    </p>
                    <Button
                      size="sm"
                      onClick={onSaveSelection}
                      disabled={selectedIdx.size === 0}
                      className="rounded-sm h-9"
                      data-testid="secop-save-selection"
                    >
                      Guardar selección
                    </Button>
                  </div>
                  <div className="border border-gray-200 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-[11px] uppercase tracking-widest text-gray-500 bg-gray-50 border-b border-gray-200">
                          <th className="px-3 py-2 text-left w-8"></th>
                          <th className="px-3 py-2 text-left">Entidad</th>
                          <th className="px-3 py-2 text-left">Objeto</th>
                          <th className="px-3 py-2 text-right whitespace-nowrap">Valor</th>
                          <th className="px-3 py-2 text-left">Adjudicatario</th>
                          <th className="px-3 py-2 text-left">Fecha</th>
                          <th className="px-3 py-2 text-left">Match</th>
                          <th className="px-3 py-2 text-left">Fuente</th>
                        </tr>
                      </thead>
                      <tbody>
                        {candidates.map((c, i) => (
                          <tr key={`itm-${i}`} data-testid={`candidate-${i}`} className="border-b border-gray-200 last:border-b-0 hover:bg-gray-50">
                            <td className="px-3 py-2">
                              <Checkbox checked={selectedIdx.has(i)} onCheckedChange={() => toggleIdx(i)} data-testid={`candidate-check-${i}`} />
                            </td>
                            <td className="px-3 py-2 text-gray-900 max-w-[180px] truncate">{c.entidad}</td>
                            <td className="px-3 py-2 text-gray-700 max-w-[300px] truncate" title={c.objeto}>{c.objeto}</td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">{c.valor_cop ? formatCOP(c.valor_cop) : "—"}</td>
                            <td className="px-3 py-2 text-gray-700 max-w-[150px] truncate">{c.adjudicatario || "—"}</td>
                            <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">{c.fecha || "—"}</td>
                            <td className="px-3 py-2" data-testid={`candidate-${i}-match`}>
                              {c._rank_score != null ? (
                                <div className="flex flex-col gap-0.5">
                                  <span className="text-xs font-semibold text-primary">{Math.round((c._rank_score || 0) * 100)}</span>
                                  {c._rank_features && (
                                    <span className="text-[10px] text-gray-500 flex flex-wrap gap-0.5">
                                      {c._rank_features.keyword_sim > 0.1 && <span title="keyword sim">kw</span>}
                                      {c._rank_features.entidad_match && <span title="entidad tipo coincide">ent</span>}
                                      {c._rank_features.region_match && <span title="región coincide">reg</span>}
                                      {c._rank_features.valor_match && <span title="valor en rango">val</span>}
                                      {c._rank_features.modalidad_match && <span title="modalidad coincide">mod</span>}
                                      {c._rank_features.feedback_boost > 0.1 && <span title="feedback histórico" className="text-emerald-600 font-semibold">fb</span>}
                                    </span>
                                  )}
                                </div>
                              ) : "—"}
                            </td>
                            <td className="px-3 py-2">
                              <a href={c.source_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">SECOP ↗</a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </section>

        {/* MANUAL */}
        <section className="mb-8">
          <Button variant="outline" onClick={() => setManualOpen(true)} className="rounded-sm border-gray-300" data-testid="secop-manual-open">
            + Agregar comparable manual
          </Button>
        </section>

        {/* STORED */}
        <section className="border border-gray-200 bg-white p-7 mb-8">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-serif text-xl text-gray-900">Comparables guardados</h2>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={onAutoSearch} disabled={autoRunning} className="rounded-sm border-primary text-primary h-9 text-xs" data-testid="secop-auto-search">
                {autoRunning ? "Buscando…" : "Búsqueda automática (SECOP II → I → vecinos → país)"}
              </Button>
              {stored.count > 0 && (
                <Button size="sm" variant="outline" onClick={onReset} className="rounded-sm border-gray-300 h-9 text-xs" data-testid="secop-reset">
                  Reset
                </Button>
              )}
            </div>
          </div>
          {autoTrace && (
            <div className="mb-4 border border-gray-200 bg-gray-50 p-3" data-testid="secop-auto-trace">
              <p className="text-xs text-gray-700">Departamento: <strong>{autoTrace.department || "no indicado"}</strong> · seleccionados: {autoTrace.selected_count}</p>
              <SecopSearchTrace steps={autoTrace.steps || []} />
            </div>
          )}

          {loadingStored && <p className="text-sm text-gray-500">Cargando…</p>}
          {!loadingStored && stored.count === 0 && !stored.limitation?.accepted_limitation && (
            <p className="text-sm text-gray-500 italic">No hay comparables guardados todavía.</p>
          )}

          {stored.limitation?.accepted_limitation && (
            <div className="border border-gray-300 bg-gray-50 p-4 mb-5 text-sm" data-testid="limitation-banner">
              <p className="font-medium text-gray-900 mb-1">
                Caso marcado sin comparables el {formatDate(stored.limitation.accepted_at)}
              </p>
              <p className="text-gray-700 italic">&ldquo;{stored.limitation.limitation_reason}&rdquo;</p>
            </div>
          )}

          {stored.items.length > 0 && (
            <div className="border border-gray-200 overflow-x-auto" data-testid="stored-comparables-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-widest text-gray-500 bg-gray-50 border-b border-gray-200">
                    <th className="px-3 py-2 text-left">Entidad</th>
                    <th className="px-3 py-2 text-left">Objeto</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">Valor</th>
                    <th className="px-3 py-2 text-left">Fecha</th>
                    <th className="px-3 py-2 text-left">Origen</th>
                    <th className="px-3 py-2 text-left">Fuente</th>
                    <th className="px-3 py-2 text-center" title="Feedback para mejorar futuras búsquedas">Feedback</th>
                    <th className="px-3 py-2 text-right">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {stored.items.map((it) => {
                    const fb = feedbackMap[it.id];
                    return (
                      <tr key={it.id} data-testid={`stored-${it.id}`} className="border-b border-gray-200 last:border-b-0">
                        <td className="px-3 py-2 max-w-[180px] truncate">{it.entidad}</td>
                        <td className="px-3 py-2 text-gray-700 max-w-[280px] truncate" title={it.objeto}>{it.objeto}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">{it.valor_cop ? formatCOP(it.valor_cop) : "—"}</td>
                        <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">{it.fecha || "—"}</td>
                        <td className="px-3 py-2">
                          {it.manual
                            ? <Badge tone="manual" testid={`badge-manual-${it.id}`}>Manual</Badge>
                            : it.verified
                            ? <Badge tone="verified" testid={`badge-verified-${it.id}`}>SECOP verificado</Badge>
                            : <Badge tone="unverified" testid={`badge-unverified-${it.id}`}>Sin verificar</Badge>}
                        </td>
                        <td className="px-3 py-2">
                          <a href={it.source_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">Ver ↗</a>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-center gap-1.5" data-testid={`feedback-${it.id}`}>
                            <button
                              type="button"
                              onClick={() => sendFeedback(it.id, true)}
                              className={`w-7 h-7 rounded-sm border text-sm transition-colors ${fb?.useful === true ? 'bg-emerald-100 border-emerald-400 text-emerald-700' : 'border-gray-300 text-gray-400 hover:bg-emerald-50 hover:text-emerald-600'}`}
                              title={fb?.useful === true ? (fb.implicit ? 'Marcado útil automáticamente (diagnóstico aprobado)' : 'Marcado útil') : 'Marcar útil'}
                              data-testid={`feedback-useful-${it.id}`}
                            >👍</button>
                            <button
                              type="button"
                              onClick={() => sendFeedback(it.id, false)}
                              className={`w-7 h-7 rounded-sm border text-sm transition-colors ${fb?.useful === false ? 'bg-rose-100 border-rose-400 text-rose-700' : 'border-gray-300 text-gray-400 hover:bg-rose-50 hover:text-rose-600'}`}
                              title="Marcar no útil"
                              data-testid={`feedback-not-useful-${it.id}`}
                            >👎</button>
                            {fb?.rating && <span className="text-[10px] text-gray-500">{fb.rating}★</span>}
                            {fb?.implicit && fb?.useful === true && <span className="text-[10px] text-gray-400" title="Implícito">·auto</span>}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button size="sm" variant="outline" onClick={() => onDelete(it.id)} className="rounded-sm border-gray-300 h-8 text-xs" data-testid={`delete-${it.id}`}>
                            Eliminar
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* LIMITATION (cuando count=0 y no aceptada todavía) */}
        {stored.count === 0 && !stored.limitation?.accepted_limitation && (
          <section className="border border-rose-200 bg-rose-50/30 p-7 mb-8" data-testid="limitation-block">
            <h3 className="font-serif text-lg text-rose-900 mb-2">¿No hay comparables disponibles?</h3>
            <p className="text-sm text-rose-900 mb-4">
              Si tras buscar y/o agregar manualmente no se encuentran comparables aplicables,
              registra explícitamente la limitación para que quede trazada en el caso.
            </p>
            <Button variant="outline" className="rounded-sm border-rose-300 text-rose-900" onClick={() => setLimitationOpen(true)} data-testid="limitation-open">
              Marcar caso sin comparables disponibles
            </Button>
          </section>
        )}
      </div>

      {/* ========== MANUAL MODAL ========== */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="rounded-sm max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif">Agregar comparable manual</DialogTitle>
            <DialogDescription>
              La URL fuente es obligatoria. Quedará marcado como manual (no verificado).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Entidad *</Label>
                <Input value={manualForm.entidad} onChange={(e) => setManualForm({ ...manualForm, entidad: e.target.value })} className="rounded-sm" data-testid="manual-entidad" />
              </div>
              <div>
                <Label>Adjudicatario</Label>
                <Input value={manualForm.adjudicatario} onChange={(e) => setManualForm({ ...manualForm, adjudicatario: e.target.value })} className="rounded-sm" data-testid="manual-adjudicatario" />
              </div>
            </div>
            <div>
              <Label>Objeto *</Label>
              <Textarea rows={2} value={manualForm.objeto} onChange={(e) => setManualForm({ ...manualForm, objeto: e.target.value })} className="rounded-sm" data-testid="manual-objeto" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label>Valor (COP)</Label>
                <Input type="number" value={manualForm.valor_cop} onChange={(e) => setManualForm({ ...manualForm, valor_cop: e.target.value })} className="rounded-sm" data-testid="manual-valor" />
              </div>
              <div>
                <Label>Fecha</Label>
                <Input type="date" value={manualForm.fecha} onChange={(e) => setManualForm({ ...manualForm, fecha: e.target.value })} className="rounded-sm" data-testid="manual-fecha" />
              </div>
              <div>
                <Label>Modalidad</Label>
                <Input value={manualForm.modalidad} onChange={(e) => setManualForm({ ...manualForm, modalidad: e.target.value })} className="rounded-sm" data-testid="manual-modalidad" />
              </div>
            </div>
            <div>
              <Label>URL fuente *</Label>
              <Input value={manualForm.source_url} onChange={(e) => setManualForm({ ...manualForm, source_url: e.target.value })} placeholder="https://..." className="rounded-sm" data-testid="manual-source-url" />
            </div>
            <div>
              <Label>Notas</Label>
              <Textarea rows={2} value={manualForm.notes} onChange={(e) => setManualForm({ ...manualForm, notes: e.target.value })} className="rounded-sm" data-testid="manual-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm border-gray-300" onClick={() => setManualOpen(false)}>Cancelar</Button>
            <Button className="rounded-sm" onClick={onAddManual} data-testid="manual-submit">Agregar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== LIMITATION MODAL ========== */}
      <Dialog open={limitationOpen} onOpenChange={setLimitationOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-serif">Marcar caso sin comparables</DialogTitle>
            <DialogDescription>Describe por qué no aplican comparables (mín. 20 caracteres).</DialogDescription>
          </DialogHeader>
          <Textarea
            rows={4}
            value={limitationReason}
            onChange={(e) => setLimitationReason(e.target.value)}
            placeholder="Ej: Proceso tan específico que no tiene precedentes consultables en SECOP por palabras clave razonables."
            className="rounded-sm"
            data-testid="limitation-reason"
          />
          <p className="text-[11px] text-gray-500">{limitationReason.length}/20 mínimo</p>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm border-gray-300" onClick={() => setLimitationOpen(false)}>Cancelar</Button>
            <Button className="rounded-sm" onClick={onAcceptLimitation} data-testid="limitation-submit">Registrar limitación</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter24.d — Confirm dialog reutilizable */}
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
}
